import React, { useEffect, useRef } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { RecentLocation } from "./searchStorage";

interface LocationSearchCardProps {
  type: "from" | "to";
  value: string;
  onChange: (code: string, name: string) => void;
  recentLocations: RecentLocation[];
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  placeholder: string;
  searchInput: string;
  setSearchInput: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    code?: string;
    name?: string;
    isDisabled?: boolean;
  }>;
  isLoading: boolean;
  label: string;
  onSearch: (inputValue: string) => Promise<void> | void;
}

const LocationSearchCard: React.FC<LocationSearchCardProps> = ({
  type,
  onChange,
  recentLocations,
  isOpen,
  onClose,
  placeholder,
  searchInput,
  setSearchInput,
  options,
  isLoading,
  onSearch,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close on outside click (only while open)
  useClickOutside(cardRef, onClose, isOpen);

  // Focus input when card opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const focusTimer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    }
  }, [isOpen]);

  const handleSelectLocation = (code: string, name: string) => {
    onChange(code, name);
    setSearchInput("");
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);
    // Filtering is local (no network call), so no debounce is needed.
    Promise.resolve(onSearch(inputValue.trim())).catch((err) => {
      console.error("Error in onSearch:", err);
    });
  };

  if (!isOpen) return null;

  const showRecentLocations =
    searchInput.trim().length === 0 && recentLocations.length > 0;
  const showSearchResults =
    searchInput.trim().length > 0 && options.length > 0;

  return (
    <div
      ref={cardRef}
      className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[600px] w-full overflow-hidden flex flex-col"
    >
      {/* Header with Close Button */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">
          {type === "from"
            ? "Select Departure Airport"
            : "Select Arrival Airport"}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i className="st-close text-lg"></i>
        </button>
      </div>

      {/* Search Input Section - At Top */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={placeholder}
            className="w-full p-3 pr-10 border border-gray-200 rounded-lg bg-lightblue-100 text-sm text-grease-100 focus:outline-none focus:ring-2 focus:ring-royalblue-100 transition-all duration-150"
            autoComplete="off"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-5 h-5 border-2 border-royalblue-100 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </div>

      {/* Content Section - Recent Locations or Search Results */}
      <div className="overflow-y-auto flex-1 max-h-[450px]">
        {/* Recent Locations Section - Show when input is empty */}
        {showRecentLocations && (
          <div>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-600 uppercase">
                {type === "from" ? "Recent Airports" : "Recent Destinations"}
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {recentLocations.map((location) => (
                <button
                  key={location.code}
                  type="button"
                  onClick={() =>
                    handleSelectLocation(location.code, location.name)
                  }
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">
                        {location.code}
                      </span>
                      <span className="text-sm text-gray-700">
                        {location.name}
                      </span>
                    </div>
                    {(location.city || location.country) && (
                      <p className="text-xs text-gray-500 mt-1">
                        {[location.city, location.country]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results - Show when typing */}
        {showSearchResults && (
          <div>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-600 uppercase">
                Search Results
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {options
                .filter((option) => !option.isDisabled)
                .map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const name = option.label.includes(" - ")
                        ? option.label.split(" - ")[1]
                        : option.label;
                      handleSelectLocation(option.value, name);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">
                        {option.value}
                      </span>
                      <span className="text-sm text-gray-700">
                        {option.label.includes(" - ")
                          ? option.label.split(" - ")[1]
                          : option.label}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* No Results Message */}
        {searchInput.trim().length > 0 &&
          !isLoading &&
          options.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No airports found
            </div>
          )}
      </div>
    </div>
  );
};

export default LocationSearchCard;
